import { useState, useEffect, useRef, useContext, type MouseEvent, type ReactNode } from "react";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl } from "../../components/GlassInput";
import { SelectLayer } from "../../components/CommonInputs";
import { MapitStateContext } from "src/states/MapitState";
import type { GeoJSONSource } from "maplibre-gl";
import { buffer } from "@turf/turf";
import { GlassButton } from "src/components/GlassButtons";
import { AreaUnit, PriceMode, AverageCalculationMethod, JPBPriceIndexSource } from "./JPBState";
import { Spinner } from "react-bootstrap";
import { MapitUtils } from "src/managers/MapitUtils";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { BsGear, BsCalendarRange, BsFileEarmarkPdf, BsBarChartLine } from "react-icons/bs";
import { TbInfoCircle } from "react-icons/tb";
import { autoUpdate, FloatingPortal, offset, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { JPBManager } from "./JPBManager";

type JPBSearchAreaProps = {
   selectableLayers: any[];
   onBufferGeometryChange: (geometry: any) => void;
   onSelectedLayerChange?: (layerId: number | undefined) => void;
   propertiesCount?: number;
   areaUnit: AreaUnit;
   onAreaUnitChange: (unit: AreaUnit) => void;
   priceMode: PriceMode;
   onPriceModeChange: (mode: PriceMode) => void;
   averageCalculationMethod: AverageCalculationMethod;
   onAverageCalculationMethodChange: (method: AverageCalculationMethod) => void;
   priceIndexSource?: JPBPriceIndexSource;
   onPriceIndexSourceChange?: (source: JPBPriceIndexSource) => void;
   onOpenPriceIndexSource?: () => void;
   onOpenTimeHorizon?: () => void;
   onExportReport?: () => void;
   isGeneratingReport?: boolean;
   onOpenSettings?: () => void;
   settingsRefreshToken?: number;
}

type VerticalChoiceOption<TValue extends string> = {
   value: TValue;
   label: ReactNode;
   ariaLabel?: string;
};

function ChoiceInfoTooltip(props: {
   title: string;
   intro: string;
   bullets: string[];
   ariaLabel: string;
}) {
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open,
      onOpenChange: setOpen,
      middleware: [offset(8)]
   });
   const { getReferenceProps, getFloatingProps } = useInteractions([
      useClick(context),
      useDismiss(context)
   ]);

   const stopOptionSelection = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
   };

   return (
      <>
         <button
            type="button"
            ref={refs.setReference}
            style={{
               display: "inline-flex",
               alignItems: "center",
               justifyContent: "center",
               padding: 0,
               margin: 0,
               border: "none",
               background: "transparent",
               color: "inherit",
               cursor: "help",
               lineHeight: 1
            }}
            aria-label={props.ariaLabel}
            {...getReferenceProps({
               onClick: stopOptionSelection,
               onMouseDown: stopOptionSelection
            })}
         >
            <TbInfoCircle size={13} />
         </button>
         {open && (
            <FloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="ViewButtonToolTip assumptions-box"
                  style={{
                     ...floatingStyles,
                     maxWidth: "420px",
                     fontSize: "0.9em",
                     lineHeight: 1.45
                  }}
                  {...getFloatingProps({
                     onClick: stopOptionSelection,
                     onMouseDown: stopOptionSelection
                  })}
               >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{props.title}</div>
                  <div style={{ marginBottom: 8 }}>{props.intro}</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                     {props.bullets.map((bullet) => (
                        <li key={bullet} style={{ marginBottom: 4 }}>
                           {bullet}
                        </li>
                     ))}
                  </ul>
               </div>
            </FloatingPortal>
         )}
      </>
   );
}

function VerticalConnectedChoice<TValue extends string>(props: {
   ariaLabel: string;
   value: TValue;
   options: readonly VerticalChoiceOption<TValue>[];
   onChange: (value: TValue) => void;
}) {
   return (
      <div className="jpb-choice" role="radiogroup" aria-label={props.ariaLabel}>
         <div className="jpb-choice__switch" aria-hidden="false">
            {props.options.map((opt) => {
               const active = opt.value === props.value;
               const a11yLabel =
                  (typeof opt.label === "string" ? opt.label : opt.ariaLabel) ?? String(opt.value);
               return (
                  <button
                     key={opt.value}
                     type="button"
                     role="radio"
                     aria-checked={active}
                     aria-label={a11yLabel}
                     className={"jpb-choice__btn" + (active ? " jpb-choice__btn--active" : "")}
                     onClick={() => props.onChange(opt.value)}
                  />
               );
            })}
         </div>
         <div className="jpb-choice__labels">
            {props.options.map((opt) => {
               const active = opt.value === props.value;
               return (
                  <span
                     key={opt.value}
                     className={"jpb-choice__label" + (active ? " jpb-choice__label--active" : "")}
                     onClick={() => props.onChange(opt.value)}
                  >
                     {opt.label}
                  </span>
               );
            })}
         </div>
      </div>
   );
}

export function JPBSearchArea(props: JPBSearchAreaProps) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const hasDebugAccess = hasAccessToFeature(Feature.Debugging);
   const hasAgroCuraIndexAccess = hasAccessToFeature(Feature.JPBAgroCuraIndexAllow);
   const hasMolbakIndexAccess = hasAccessToFeature(Feature.JPBMolbakIndexAllow);
   const defaultRadiusKm = (SettingsManager.getSystemSetting("jpb") && (SettingsManager.getSystemSetting("jpb") as any).defaultRadiusKm) || 10;
   const [selectedLayer, setSelectedLayer] = useState<number | undefined>(undefined);
   const [bufferDistance, setBufferDistance] = useState<number>(defaultRadiusKm);
   const previousDefaultRadiusRef = useRef<number>(defaultRadiusKm);
   const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
   const exportLinkRef = useRef<HTMLAnchorElement | null>(null);
   // Cache buffer distances per layer ID
   const bufferCacheRef = useRef<Map<number, number>>(new Map());


   const getDefaultBufferForLayer = (layerId: number | undefined): number => {
      if (layerId === undefined) return defaultRadiusKm;
      const layer = mapitState.layers[layerId];
      if (!layer) return defaultRadiusKm;
      return MapitUtils.isPointLayer(layer.type) ? 10 : 0;
   };

   useEffect(() => {
      if (selectedLayer !== undefined) {
         if (bufferCacheRef.current.has(selectedLayer)) {
            setBufferDistance(bufferCacheRef.current.get(selectedLayer)!);
         } else {
            const defaultBuffer = getDefaultBufferForLayer(selectedLayer);
            setBufferDistance(defaultBuffer);
            bufferCacheRef.current.set(selectedLayer, defaultBuffer);
         }
      }
   }, [selectedLayer, mapitState.layers]);

   useEffect(() => {
      const previousDefault = previousDefaultRadiusRef.current;
      const nextDefault = (SettingsManager.getSystemSetting("jpb") && (SettingsManager.getSystemSetting("jpb") as any).defaultRadiusKm) || 10;
      previousDefaultRadiusRef.current = nextDefault;

      if (selectedLayer === undefined && bufferDistance === previousDefault) {
         setBufferDistance(nextDefault);
      }
   }, [props.settingsRefreshToken, selectedLayer, bufferDistance]);

   // Removed auto-selection of first layer - user must manually select to avoid accidentally choosing large layers
   // useEffect(() => {
   //    if (selectedLayer === undefined && props.selectableLayers.length > 0) {
   //       setSelectedLayer(props.selectableLayers[0].layerId);
   //    }
   // }, [props.selectableLayers, selectedLayer]);

   useEffect(() => {
      props.onSelectedLayerChange?.(selectedLayer);
   }, [selectedLayer, props.onSelectedLayerChange]);

   useEffect(() => {
      if (mapitState.map) {
         const map = mapitState.map.getMapPop();
         addSearchAreaOutline(map, "jpb-search-area", "#d6c251");
         return () => {
            removeSearchAreaLayer(map, "jpb-search-area");
         };
      }
   }, [mapitState.map]);

   // Update search area geometry when layer or buffer changes
   useEffect(() => {
      if (mapitState.map && selectedLayer) {
         const geom = mapitState.layers[selectedLayer]?.geoJson;
         const map = mapitState.map.getMapPop();
         if (geom) {
            const bufferedGeom = buffer(geom, bufferDistance || 0, { units: 'kilometers' });
            const combinedBufferGeom = JPBManager.toCombinedSearchGeometry(bufferedGeom) || bufferedGeom;
            setSearchAreaGeometry(map, "jpb-search-area", combinedBufferGeom);

            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
               props.onBufferGeometryChange(combinedBufferGeom);
            }, 500);
         } else {
            setSearchAreaGeometry(map, "jpb-search-area", null);
            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }
            props.onBufferGeometryChange(null);
         }
      } else {
         if (mapitState.map) {
            const map = mapitState.map.getMapPop();
            setSearchAreaGeometry(map, "jpb-search-area", null);
            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }
            props.onBufferGeometryChange(null);
         }
      }

      return () => {
         if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
         }
      };
   }, [mapitState.map, selectedLayer, bufferDistance, props.onBufferGeometryChange]);

   const availablePriceIndexOptions: VerticalChoiceOption<JPBPriceIndexSource>[] = [
      ...(hasMolbakIndexAccess
         ? [{
            value: JPBPriceIndexSource.Molbak,
            label: (
               <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span>{Localization.getText("Mølbak index")}</span>
                  <ChoiceInfoTooltip
                     title={Localization.getText("About the Mølbak index")}
                     intro={Localization.getText("info:molbakIndexIntro")}
                     bullets={[
                        Localization.getText("info:molbakIndexPoint1"),
                        Localization.getText("info:molbakIndexPoint2"),
                        Localization.getText("info:molbakIndexPoint3")
                     ]}
                     ariaLabel={Localization.getText("Show information about the Mølbak index")}
                  />
               </span>
            ),
            ariaLabel: Localization.getText("Mølbak index")
         }]
         : []),
      ...(hasAgroCuraIndexAccess
         ? [{ value: JPBPriceIndexSource.AgroCura, label: Localization.getText("AgroCura index") }]
         : []),
      { value: JPBPriceIndexSource.None, label: Localization.getText("No index") }
   ];
   const availablePriceIndexSources = new Set(availablePriceIndexOptions.map((option) => option.value));
   const selectedPriceIndexSource = availablePriceIndexSources.has(props.priceIndexSource ?? JPBPriceIndexSource.None)
      ? (props.priceIndexSource ?? JPBPriceIndexSource.None)
      : JPBPriceIndexSource.None;

   return (
      <>
         <div style={{ position: "relative", width: "100%" }}>
            <a ref={exportLinkRef} style={{ opacity: 0, position: "absolute", zIndex: -2 }}></a>
            <div className="jpb-search-area-layout">
               <div className="jpb-search-area-row">
                  <div className="jpb-search-area-left">
                     <div className="jpb-choice-stack">
                        <VerticalConnectedChoice
                           ariaLabel={Localization.getText("Area unit")}
                           value={props.areaUnit}
                           onChange={(unit) => props.onAreaUnitChange(unit)}
                           options={[
                              { value: AreaUnit.SquareMeter, label: "m²" },
                              { value: AreaUnit.Hectare, label: "ha" }
                           ] as const}
                        />
                        <VerticalConnectedChoice
                           ariaLabel={Localization.getText("Price mode")}
                           value={props.priceMode}
                           onChange={(mode) => props.onPriceModeChange(mode)}
                           options={[
                              { value: PriceMode.Standard, label: Localization.getText("Whole property area") },
                              { value: PriceMode.Landbrug, label: Localization.getText("Only farmland area") }
                           ] as const}
                        />
                        <VerticalConnectedChoice
                           ariaLabel={Localization.getText("Average method")}
                           value={props.averageCalculationMethod}
                           onChange={(method) => props.onAverageCalculationMethodChange(method)}
                           options={[
                              { value: AverageCalculationMethod.WeightedByArea, label: Localization.getText("Weighted by area") },
                              { value: AverageCalculationMethod.EqualPerSample, label: Localization.getText("Equal per transaction") }
                           ] as const}
                        />
                        <VerticalConnectedChoice
                           ariaLabel={Localization.getText("Price index")}
                           value={selectedPriceIndexSource}
                           onChange={(source) => props.onPriceIndexSourceChange?.(source)}
                           options={availablePriceIndexOptions}
                        />
                     </div>
                  </div>

                  <div className="jpb-search-area-inputs">
                     <div className="jpb-main-controls-split">
                        <div className="jpb-main-controls-half jpb-main-controls-half--left">
                           <div className="jpb-search-area-select jpb-main-controls-select">
                              <SelectLayer
                                 selectedLayerId={selectedLayer || ""}
                                 onSelect={(layerId) => {
                                    if (layerId !== undefined) {
                                       setSelectedLayer(layerId);
                                    }
                                 }}
                                 availableLayers={props.selectableLayers}
                              />
                           </div>
                        </div>
                        <div className="jpb-main-controls-half jpb-main-controls-half--right">
                           <div className="jpb-search-area-select jpb-main-controls-select">
                              <GlassInputControl>
                                 <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={bufferDistance}
                                    onChange={(e) => {
                                       const value = Number(e.target.value);
                                       const newValue = value >= 0 ? value : 0;
                                       setBufferDistance(newValue);
                                       if (selectedLayer !== undefined) {
                                          bufferCacheRef.current.set(selectedLayer, newValue);
                                       }
                                    }}
                                 />
                                 <span>{Localization.getText("Km")}</span>
                              </GlassInputControl>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="jpb-search-area-right">
                     {props.onOpenSettings && hasAccessToFeature(Feature.JPBTool) && (
                        <GlassButton
                           className="jpb-search-area-btn-icon"
                           title={Localization.getText("JPB Settings")}
                           onClick={() => props.onOpenSettings?.()}
                        >
                           <BsGear size={18} />
                        </GlassButton>
                     )}
                     {props.onOpenPriceIndexSource && hasAccessToFeature(Feature.JPBTool) && hasDebugAccess && (
                        <GlassButton
                           className="jpb-search-area-btn-icon"
                           title={Localization.getFormattedText("Price index source ({source})", {
                              source: JPBManager.getPriceIndexSourceLabel(props.priceIndexSource ?? JPBPriceIndexSource.AgroCura)
                           })}
                           onClick={() => props.onOpenPriceIndexSource?.()}
                        >
                           <BsBarChartLine size={18} />
                        </GlassButton>
                     )}
                     <GlassButton
                        className="jpb-search-area-btn-icon"
                        title={Localization.getText("Calculation period")}
                        onClick={() => props.onOpenTimeHorizon?.()}
                     >
                        <BsCalendarRange size={18} />
                     </GlassButton>
                     {(props.propertiesCount ?? 0) >= 2 && hasAccessToFeature(Feature.JPBRapport) && (
                        <GlassButton
                           className="jpb-search-area-btn-icon"
                           title={Localization.getText("Export PDF")}
                           onClick={() => {
                              if (props.onExportReport && !props.isGeneratingReport) {
                                  props.onExportReport();
                              }
                           }}
                           disabled={props.isGeneratingReport}
                        >
                           {props.isGeneratingReport ? (
                              <Spinner
                                 animation="border"
                                 size="sm"
                                 variant="light"
                                 style={{ width: "1em", height: "1em", borderWidth: "0.15em" }}
                              />
                           ) : (
                              <BsFileEarmarkPdf size={18} />
                           )}
                        </GlassButton>
                     )}
                  </div>
               </div>
            </div>
         </div>
      </>
   );
}



// MARK: Helpers
function removeSearchAreaLayer(map: any, id: string) {
   if (map.getLayer(id + '-fill')) {
      map.removeLayer(id + '-fill');
   }
   if (map.getLayer(id + '-outline')) {
      map.removeLayer(id + '-outline');
   }
   if (map.getSource(id)) {
      map.removeSource(id);
   }
}

function addSearchAreaOutline(map: any, id: string, color: string) {
   if (!map.getSource(id)) {
      map.addSource(id, {
         type: 'geojson',
         data: {
            type: 'FeatureCollection',
            features: []
         }
      });
   }
   if (!map.getLayer(id + '-outline')) {
      map.addLayer({
         id: id + '-outline',
         type: 'line',
         source: id,
         paint: {
            'line-color': color,
            'line-width': 2
         }
      });
   }
}

function setSearchAreaGeometry(map: any, id: string, geom?: any | null) {
   const source = map.getSource(id) as GeoJSONSource;
   if (source) {
      if (!geom) {
         source.setData({
            type: 'FeatureCollection',
            features: []
         } as any);
         return;
      }
      source.setData(geom as any);
   }
}
